/**
 * About / Mission Section with parallax scroll effect
 */
import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'motion/react';
import { Target, Eye, Heart } from 'lucide-react';
import { content } from '@/landing/data/content';

export function About() {
  const sectionRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });

  const y = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const opacity = useTransform(scrollYProgress, [0, 0.5, 1], [0.4, 1, 0.4]);

  return (
    <section id="about" ref={sectionRef} className="relative py-24 lg:py-32 overflow-hidden bg-[var(--navy)] border-t border-[rgba(201,168,76,0.15)]">
      {/* Parallax Background Image */}
      <motion.div
        style={{ y, opacity }}
        className="absolute inset-0 -z-10"
      >
        <div
          className="w-full h-[120%] bg-cover bg-center opacity-10"
          style={{
            backgroundImage: `url(https://images.unsplash.com/photo-1758691736843-90f58dce465e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx0ZWFtJTIwY29sbGFib3JhdGlvbiUyMGlubm92YXRpb258ZW58MXx8fHwxNzcxNDk3OTMyfDA&ixlib=rb-4.1.0&q=80&w=1080)`,
          }}
        />
      </motion.div>

      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1637856794303-d864ce316444?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1080"
                alt="People collaborating at a table with a laptop"
                className="w-full h-auto"
              />
              {/* Glassmorphic overlay */}
              <div className="absolute inset-0 bg-gradient-to-tr from-[var(--royal)]/20 to-[var(--gold)]/20" />
            </div>

            {/* Floating card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="absolute -bottom-6 -right-6 bg-[var(--navy)]/90 backdrop-blur-lg rounded-2xl shadow-xl p-6 border border-[rgba(201,168,76,0.25)]"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-[var(--gold)] flex items-center justify-center">
                  <Heart className="w-6 h-6 text-[var(--navy)]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">100%</p>
                  <p className="text-sm text-[var(--text-muted)]">Transparent</p>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right: Content */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="font-display text-4xl md:text-5xl font-semibold text-[var(--gold)] mb-6">
                About INSOLIP
              </h2>
              <p className="text-lg text-[var(--text-muted)] mb-8 leading-relaxed font-light">
                {content.longDescription}
              </p>
            </motion.div>

            {/* Mission & Vision */}
            <div className="space-y-6">
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="flex gap-4 group"
              >
                <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-[rgba(26,86,219,0.3)] border border-[rgba(201,168,76,0.2)] flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Target className="w-7 h-7 text-[var(--royal)]" />
                </div>
                <div>
                  <h3 className="font-display text-xl font-semibold text-white mb-2">Our Mission</h3>
                  <p className="text-[var(--text-muted)] leading-relaxed font-light">
                    {content.mission}
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4, duration: 0.6 }}
                className="flex gap-4 group"
              >
                <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-[var(--gold-pale)] border border-[rgba(201,168,76,0.25)] flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Eye className="w-7 h-7 text-[var(--gold)]" />
                </div>
                <div>
                  <h3 className="font-display text-xl font-semibold text-white mb-2">Our Vision</h3>
                  <p className="text-[var(--text-muted)] leading-relaxed font-light">
                    {content.vision}
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Values Pills */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="flex flex-wrap gap-3 mt-8"
            >
              {content.values.map((value) => (
                <motion.div
                  key={value.name}
                  whileHover={{ scale: 1.05 }}
                  className="px-4 py-2 rounded-full bg-[var(--gold-pale)] border border-[rgba(201,168,76,0.25)] cursor-pointer"
                >
                  <span className="text-sm font-semibold text-[var(--gold)]">
                    {value.name}
                  </span>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
